import { MERCHANT_OPERATIONS } from "@price-rescue/api-contracts";
import type { MerchantOperationQuery } from "@price-rescue/api-contracts";

import type { MerchantApiClient } from "../../types.js";
import type {
  CompetitorDomain,
  CompetitorFilters,
  CompetitorFormDraft,
  CompetitorPage,
  CompetitorTestResult,
} from "./models.js";

interface DataEnvelope<T> {
  data: T;
  meta: { requestId: string };
}

interface ListEnvelope {
  items: CompetitorDomain[];
  nextCursor: string | null;
  meta: { requestId: string };
}

export interface CompetitorsApi {
  createCompetitor(draft: CompetitorFormDraft, idempotencyKey: string): Promise<CompetitorDomain>;
  deleteCompetitor(competitorId: string, idempotencyKey: string): Promise<CompetitorDomain>;
  getCompetitor(competitorId: string): Promise<CompetitorDomain>;
  listCompetitors(filters: CompetitorFilters): Promise<CompetitorPage>;
  testCompetitor(competitorId: string, idempotencyKey: string): Promise<CompetitorTestResult>;
  updateCompetitor(
    competitorId: string,
    draft: CompetitorFormDraft,
    idempotencyKey: string,
  ): Promise<CompetitorDomain>;
}

export function createCompetitorsApi(client: MerchantApiClient): CompetitorsApi {
  return {
    async createCompetitor(draft, idempotencyKey) {
      return unwrap(
        await client.operation<DataEnvelope<CompetitorDomain>>(
          MERCHANT_OPERATIONS.createCompetitor,
          { body: toApiDraft(draft), request: { idempotencyKey } },
        ),
      );
    },
    async deleteCompetitor(competitorId, idempotencyKey) {
      return unwrap(
        await client.operation<DataEnvelope<CompetitorDomain>>(
          MERCHANT_OPERATIONS.deleteCompetitor,
          {
            pathParams: { competitorId },
            request: { idempotencyKey },
          },
        ),
      );
    },
    async getCompetitor(competitorId) {
      return unwrap(
        await client.operation<DataEnvelope<CompetitorDomain>>(MERCHANT_OPERATIONS.getCompetitor, {
          pathParams: { competitorId },
        }),
      );
    },
    async listCompetitors(filters) {
      const envelope = await client.operation<ListEnvelope>(MERCHANT_OPERATIONS.listCompetitors, {
        query: toOperationQuery(filters),
      });
      return { competitors: envelope.items, nextCursor: envelope.nextCursor };
    },
    async testCompetitor(competitorId, idempotencyKey) {
      return unwrap(
        await client.operation<DataEnvelope<CompetitorTestResult>>(
          MERCHANT_OPERATIONS.testCompetitorRetrieval,
          { body: {}, pathParams: { competitorId }, request: { idempotencyKey } },
        ),
      );
    },
    async updateCompetitor(competitorId, draft, idempotencyKey) {
      return unwrap(
        await client.operation<DataEnvelope<CompetitorDomain>>(
          MERCHANT_OPERATIONS.updateCompetitor,
          {
            body: toApiDraft(draft),
            pathParams: { competitorId },
            request: { idempotencyKey },
          },
        ),
      );
    },
  };
}

function toApiDraft(draft: CompetitorFormDraft): Record<string, unknown> {
  return {
    name: draft.name,
    domain: draft.domain,
    approvedDomain: draft.approvedDomain,
    competitorStatus: draft.competitorStatus,
    automaticMatching: draft.automaticMatching,
    domainMatchMode: draft.domainMatchMode,
    approvedSubdomainPatterns: draft.approvedSubdomainPatterns,
    notes: draft.notes,
  };
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

function toOperationQuery(filters: CompetitorFilters): MerchantOperationQuery {
  const { automaticMatching, competitorStatus, cursor, limit, search } = filters;
  const safeSearch = toSafeSearchQuery(search);
  return {
    ...(safeSearch === undefined ? {} : { search: safeSearch }),
    ...(competitorStatus === undefined ? {} : { competitorStatus }),
    ...(automaticMatching === undefined ? {} : { automaticMatching }),
    ...(cursor === undefined ? {} : { cursor }),
    ...(limit === undefined ? {} : { limit }),
  } as unknown as MerchantOperationQuery;
}

function toSafeSearchQuery(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed.length === 0 ? undefined : trimmed.slice(0, 160);
}
